/**
 * Common types and enums shared across Harmony controls
 *
 * This module provides standardized type definitions for common properties used across multiple control components,
 * promoting consistency and reducing duplication.
 */

/**
 * Standard size variants used across multiple controls
 *
 * @example
 *   (xs, sm, md, lg, xl);
 */
export type HxControlSize = '6xs' | '5xs' | '4xs' | '3xs' | '2xs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

/** Common visual variants for controls Defines the visual weight/style hierarchy */
export type HxControlVariant = 'fill' | 'soft' | 'outline' | 'ghost';

/** Semantic context colors used for conveying meaning Aligned with Moon Design System color semantics */
export type HxControlContext = 'brand' | 'neutral' | 'positive' | 'negative' | 'caution' | 'info';

/** Common position values for popovers, dropdowns, tooltips */
export type HxPosition =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end';

/** Control state flags for interactive elements */
export interface HxControlState {
  /** Whether the control is disabled */
  disabled?: boolean;
  /** Whether the control is in an error state */
  error?: boolean;
  /** Whether the control is readonly */
  readonly?: boolean;
  /** Whether the control is focused */
  focused?: boolean;
  /** Whether the control is loading */
  loading?: boolean;
  /** Whether the control has been touched (form controls) */
  touched?: boolean;
}

/** Base interface for control change events Controls can extend this to add specific event data */
export interface HxControlChangeEvent<T = any> {
  /** The new value of the control */
  value: T;
  /** The source control that emitted the event */
  source?: any;
}

/** Common interface for option-based controls (select, autocomplete, etc.) */
export interface HxOption<T = any> {
  /** The value of the option */
  value: T;
  /** Display label for the option */
  label: string;
  /** Whether the option is disabled */
  disabled?: boolean;
  /** Optional icon for the option */
  icon?: string;
  /** Optional title for hover tooltip (defaults to label if not provided) */
  title?: string;
}

/** Option group for organizing options hierarchically */
export interface HxOptionGroup<T = any> {
  /** Group label */
  label: string;
  /** Options within the group */
  options: HxOption<T>[];
  /** Whether the entire group is disabled */
  disabled?: boolean;
}

/**
 * Control type classification for organizing controls by purpose
 *
 * - BasicControl: Standard form controls and interactive elements (inputs, buttons, etc.)
 * - LayoutControl: Structural and layout components (sections, cards, dividers, etc.)
 * - ClinicalControl: Clinical calculation and medical-specific components
 */
export enum ControlType {
  BasicControl = 'BasicControl',
  LayoutControl = 'LayoutControl',
  ClinicalControl = 'ClinicalControl',
}
